import { Button, cn, Popover, PopoverContent, PopoverTrigger } from "@tellescope/ui";
import type { FreeformFilterBlock as FilterBlock } from "@tellescope/calendar";
import { Check, ChevronDown, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FreeformFilterRow } from "../../../components/tables/FreeformFilterRow";
import type { FreeformFieldOption, FreeformFilter } from "../../../components/tables/freeformFilters";
import { Fragment } from "react/jsx-runtime";

export interface FreeformFilterBlockProps {
  block: FilterBlock;
  fieldOptions: FreeformFieldOption[];
  onChange: (block: FilterBlock) => void;
  onRemove: () => void;
}

const makeEmptyFilter = (fieldOptions: FreeformFieldOption[]): FreeformFilter => ({
  field: fieldOptions[0]?.value ?? "",
  operation: "equals",
  value: undefined,
});

export const FreeformFilterBlockEditor = ({ block, fieldOptions, onChange, onRemove }: FreeformFilterBlockProps) => {
  const { t } = useTranslation("calendar");

  const updateFilter = (index: number, next: FreeformFilter) => {
    onChange({
      ...block,
      filters: block.filters.map((filter, i) => (i === index ? next : filter)),
    });
  };

  const removeFilter = (index: number) => {
    onChange({
      ...block,
      filters: block.filters.filter((_, i) => i !== index),
    });
  };

  const addFilter = () => {
    onChange({
      ...block,
      filters: [...block.filters, makeEmptyFilter(fieldOptions)],
    });
  };

  return (
    <>
      <div className="flex flex-col items-center">
        {block.filters.map((filter, index) => (
          <Fragment key={`${block.id}-${index}-${filter.field}`}>
            <FreeformFilterRow
              filter={filter}
              fieldOptions={fieldOptions}
              onChange={(next) => updateFilter(index, next)}
              onRemove={() => {
                if (block.filters.length === 1) {
                  onRemove();
                } else {
                  removeFilter(index);
                }
              }}
              variant={block.filters.length > 1 ? block.logic : "and"}
            />
            {index !== block.filters.length - 1 && (
              <>
                <div className={cn("h-2 w-[2px] bg-orange-300", block.logic === "and" && "bg-transparent")} />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="secondary"
                      className={cn(
                        "text-xs rounded-full text-muted-foreground gap-1",
                        block.logic === "or" ? "bg-orange-100 text-orange-700 hover:bg-orange-200" : "bg-gray-100"
                      )}
                    >
                      {block.logic === "or" ? t("OR") : t("AND")}
                      <ChevronDown className={block.logic === "or" ? "text-orange-700" : "text-muted-foreground"} />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="flex flex-col w-[100px] items-start p-1" align="start">
                    <Button
                      variant="ghost"
                      className="text-xs font-light justify-between w-full"
                      onClick={() =>
                        onChange({
                          ...block,
                          logic: "and",
                        })
                      }
                    >
                      {t("AND")}
                      {block.logic === "and" && <Check />}
                    </Button>
                    <Button
                      variant="ghost"
                      className="text-xs font-light justify-between w-full"
                      onClick={() =>
                        onChange({
                          ...block,
                          logic: "or",
                        })
                      }
                    >
                      {t("OR")}
                      {block.logic === "or" && <Check />}
                    </Button>
                  </PopoverContent>
                </Popover>
                <div className={cn("h-2 w-[2px] bg-orange-300", block.logic === "and" && "bg-transparent")} />
              </>
            )}
          </Fragment>
        ))}
      </div>
      <div className="flex justify-between items-center py-2 w-full">
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground text-xs items-center flex leading-none rounded-full h-auto px-2 py-1 font-light"
          onClick={addFilter}
        >
          <Plus className="h-4 w-4" />
          {t("Add condition")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground text-xs items-center flex leading-none rounded-full h-auto px-2 py-1 font-light"
          onClick={onRemove}
        >
          {t("Clear")}
        </Button>
      </div>
    </>
  );
};
